import { useId } from "react";
import { Alert } from "../alert";
import { Zustandspille } from "../zustandspille";
import { Feldhilfe, type FeldhilfeInhalt } from "./feldhilfe";

export type { FeldhilfeInhalt } from "./feldhilfe";

/**
 * Feld (Musterkatalog M-002, Design-Guide `formularfelder.md`): die Hülle um jedes Formularsteuerelement.
 * Jede Eingabe wird über `Feld` gebaut (Sperrklinke `check:ui-preline` Regel 14).
 *
 * - F1 Label oben (`layout="oben"`, Standard) oder links (`layout="links"`, Einstellungsseiten; unter 640 px
 *   klappt die Familie B zu Label oben).
 * - F2 Größe `standard` oder `kompakt`.
 * - F5 Pflicht ist unmarkiert (kein Sternchen); optionale Felder tragen „Optional“ rechts am Label.
 * - F3 Feldfehler (Wert falsch, behebbar, r100): Rahmen rot, der Satz in einer Pille mit Punkt, kein Symbol im Feld
 *   (Zustandspille `gefahr` mit `mitPunkt`, M-001). Abweichung vom Original `input-validation-states`: AW-005.
 * - F4 Ablehnung/Fehlschlag (System lehnt ab oder scheitert, r105): `hinweissatz` unter dem Feld als Alert Ton
 *   „warnung“, nur der Wortlaut unterscheidet beide Fälle. Feldfehler und Hinweissatz erscheinen nie zugleich
 *   (r100): ist `fehler` gesetzt, entfällt der Hinweissatz (erst validieren, dann abschicken).
 *
 * Der Baustein prüft keine Rechte. Der Aufrufer entscheidet mit derselben Prüffunktion wie die Action (r109),
 * welcher Satz erscheint, und füllt Auswahllisten nur mit dem, was die Action erlaubt (r110).
 */
export type FeldLayout = "oben" | "links";
/**
 * `ruhig` (Aufgaben-Detail A4′, Betreiber 14.10.2026, Eigenschaften): Eingabe in der Größe `kompakt`, aber ohne Rahmen und Grund
 * im Ruhezustand; Rahmen und Grund erscheinen erst bei Hover und bei Fokus (der Fokusrahmen ist immer sichtbar). Unterstützt von
 * `Textfeld`, `Datumsfeld`, `Auswahl` und der Einzel-Personenauswahl; alle übrigen Bausteine behandeln `ruhig` wie `kompakt`.
 */
export type FeldGroesse = "standard" | "kompakt" | "ruhig";
/** Die Größen, die jeder Baustein kennt (`ruhig` zählt dort als `kompakt`). */
export type FeldGroesseGrund = Exclude<FeldGroesse, "ruhig">;
export const grundGroesse = (g: FeldGroesse): FeldGroesseGrund => (g === "ruhig" ? "kompakt" : g);

/** Attribute, die das Steuerelement an sein Feld bindet (Label, Beschreibung, Fehlerzustand). */
export interface FeldAttribute {
  id: string;
  "aria-describedby"?: string;
  "aria-invalid"?: true;
  groesse: FeldGroesse;
  fehlerhaft: boolean;
  /** Id der Beschriftung; bei Gruppen (`gruppe`) per `aria-labelledby` zu verwenden. */
  labelId: string;
}

/** Gemeinsame Eigenschaften aller Feld-Bausteine. */
export interface FeldEigenschaften {
  /** Eindeutige Id; fehlt sie, erzeugt `useId` eine. */
  id?: string;
  label: string;
  /** Label nur für Hilfstechnik (`sr-only`), z. B. Suchfeld in der Listenkopf-Zeile. */
  labelVersteckt?: boolean;
  /** Optionales Feld: „Optional“ rechts am Label (F5). Pflichtfelder bleiben unmarkiert. */
  optional?: boolean;
  /** Hilfetext unter dem Feld. */
  hilfe?: string;
  /**
   * Feldhilfe (M-060, `feldhilfe.md`): „?“ am Label, Klick/Tipp öffnet „Wozu?“ + „Beispiel“. Nur Katalogeinträge des Moduls
   * (`definiereFeldhilfen`), nie eigene Texte am Aufrufer. Ergänzt `hilfe` (kurzer Pflichthinweis, bleibt sichtbar), ersetzt es nicht.
   * Ohne sichtbares Label (`labelVersteckt`) entfällt der Auslöser.
   */
  feldhilfe?: FeldhilfeInhalt;
  /** Feldfehler (F3): kurzer Satz, den der Nutzer durch erneute Eingabe beheben kann. */
  fehler?: string;
  /** Ablehnung oder Fehlschlag (F4, r105): ganzer Satz; entfällt, solange `fehler` gesetzt ist. */
  hinweissatz?: string;
  layout?: FeldLayout;
  groesse?: FeldGroesse;
}

const LABEL = "block text-sm font-medium text-foreground";
const LABEL_VERSTECKT = "sr-only";
const OPTIONAL_MARKE = "block text-sm text-muted-foreground-1";
const KOPFZEILE_OBEN = "mb-2 flex flex-wrap justify-between items-center gap-2";
const KOPFZEILE_LINKS = "flex flex-wrap justify-between items-center gap-2 sm:pt-2.5";
const HILFE = "mt-2 text-sm text-muted-foreground-1";
const MELDUNG = "mt-2";

/** Id ohne Doppelpunkte (`useId` liefert `:r1:`), damit sie auch als CSS-Selektor taugt. */
export function useFeldId(vorgabe?: string): string {
  const auto = useId();
  return vorgabe ?? `f${auto.replace(/:/g, "")}`;
}

/** Schaltet die Beschreibungs- und Fehler-Ids zusammen. */
export function feldIds(id: string) {
  return { hilfe: `${id}-hilfe`, fehler: `${id}-fehler`, hinweis: `${id}-hinweis` };
}

export function Feld({
  id: idVorgabe,
  label,
  labelVersteckt = false,
  optional = false,
  hilfe,
  feldhilfe,
  fehler,
  hinweissatz,
  layout = "oben",
  groesse = "standard",
  gruppe = false,
  beispielInMehrzeiler = false,
  children,
}: FeldEigenschaften & {
  /** Beschriftung einer Gruppe von Steuerelementen (kein einzelnes Eingabeelement): Text statt `<label for>`. */
  gruppe?: boolean;
  /** Nur `Mehrzeiler` setzt es: „Beispiel übernehmen“ darf in dieses Feld schreiben (FQ5), solange es leer ist. */
  beispielInMehrzeiler?: boolean;
  children: (a: FeldAttribute) => React.ReactNode;
}) {
  const id = useFeldId(idVorgabe);
  const ids = feldIds(id);
  const fehlerhaft = Boolean(fehler);
  const zeigeHinweis = Boolean(hinweissatz) && !fehlerhaft;
  const beschreibung = [hilfe ? ids.hilfe : null, fehlerhaft ? ids.fehler : null, zeigeHinweis ? ids.hinweis : null]
    .filter(Boolean)
    .join(" ");
  const attribute: FeldAttribute = {
    id,
    "aria-describedby": beschreibung || undefined,
    "aria-invalid": fehlerhaft ? true : undefined,
    groesse,
    fehlerhaft,
    labelId: `${id}-label`,
  };

  const beschriftung = (
    <>
      {labelVersteckt ? (
        gruppe ? (
          <span id={attribute.labelId} className={LABEL_VERSTECKT}>
            {label}
          </span>
        ) : (
          <label htmlFor={id} id={attribute.labelId} className={LABEL_VERSTECKT}>
            {label}
          </label>
        )
      ) : (
        <div className={layout === "links" ? KOPFZEILE_LINKS : KOPFZEILE_OBEN}>
          {feldhilfe ? (
            <span className="inline-flex items-center gap-x-1.5">
              {gruppe ? (
                <span id={attribute.labelId} className={LABEL}>
                  {label}
                </span>
              ) : (
                <label htmlFor={id} id={attribute.labelId} className={LABEL}>
                  {label}
                </label>
              )}
              <Feldhilfe label={label} inhalt={feldhilfe} mehrzeilerId={beispielInMehrzeiler ? id : undefined} />
            </span>
          ) : gruppe ? (
            <span id={attribute.labelId} className={LABEL}>
              {label}
            </span>
          ) : (
            <label htmlFor={id} id={attribute.labelId} className={LABEL}>
              {label}
            </label>
          )}
          {optional && <span className={OPTIONAL_MARKE}>Optional</span>}
        </div>
      )}
    </>
  );

  const unterhalb = (
    <>
      {hilfe && (
        <p id={ids.hilfe} className={HILFE}>
          {hilfe}
        </p>
      )}
      {fehlerhaft && (
        <p id={ids.fehler} className={MELDUNG}>
          {/* Abweichung AW-005: Feldfehler V2 (Rahmen rot, Satz in Pille mit Punkt, kein Symbol) statt Original input-validation-states */}
          <Zustandspille ton="gefahr" mitPunkt>{fehler}</Zustandspille>
        </p>
      )}
      {zeigeHinweis && (
        <div className={MELDUNG}>
          <Alert ton="warnung" id={ids.hinweis}>
            {hinweissatz}
          </Alert>
        </div>
      )}
    </>
  );

  return (
    <>
      {/* Preline: component/input/input-corner-hint */}
      {layout === "links" ? (
        <div className="sm:grid sm:grid-cols-3 sm:gap-x-6 space-y-2 sm:space-y-0">
          <div className="sm:col-span-1">{beschriftung}</div>
          <div className="sm:col-span-2">
            {children(attribute)}
            {unterhalb}
          </div>
        </div>
      ) : (
        <div>
          {beschriftung}
          {children(attribute)}
          {unterhalb}
        </div>
      )}
    </>
  );
}

/** Rahmen um ein Eingabeelement. Der Feldfehler (F3) zeigt sich als roter Rahmen und Pille, nie als Symbol im Feld. */
export function FeldHuelle({ children }: { children: React.ReactNode }) {
  return (
    <>
      {/* Preline: component/input/input-validation-states */}
      <div className="relative">{children}</div>
    </>
  );
}
